import { useEffect, useState } from 'react';
import {
  useAdvanceSummary,
  getVideoFeedsSummary,
  getSteamingFeedsSummary,
} from '../../utils';
import { PromptConfirmItemType } from '../../types';
import useCommonGptData, { CommonGptDataType } from './useCommonGptData';
import { aigcCreateVideoSummary } from '@op/services/aigc';

export function useRequestGpt(
  props: CommonGptDataType & {
    autoRequest?: boolean;
    filter: any;
    savePrefix?: string;
  },
) {
  const { filter, autoRequest, promptInfo, savePrefix } = props;

  const { getAdvanceSummaryHooks } = useAdvanceSummary();

  const {
    status,
    percent,
    renderHtml,
    isEmpty,
    handleStop,
    summary,
    errorType,
    GPTSentiment,
    handleSuccess,
    handleError,
    setPageStatus,
    loadingBefore,
    origSummary,
  } = useCommonGptData(props);

  const requestGptData = async (
    promptInfo: PromptConfirmItemType,
    defaultPercent = 0,
  ) => {
    const stopProps = loadingBefore(promptInfo, defaultPercent);
    const { data, status, tips } =
      (await getAdvanceSummaryHooks({
        filter: {
          ...filter,
          task_id: promptInfo?.taskId,
        },
        promptInfo,
        savePrefix,
        ...stopProps,
      })) || {};
    if (status === 'cancel') {
      setPageStatus({ status: 'cancel' });
    } else if (status === 'success') {
      handleSuccess(data || '');
    } else {
      handleError(tips);
    }
  };

  useEffect(() => {
    if (autoRequest && promptInfo) {
      requestGptData(promptInfo);
    }
  }, [promptInfo]);

  return {
    status,
    requestGptData,
    percent,
    renderHtml,
    isEmpty,
    handleStop,
    summary,
    errorType,
    GPTSentiment,
    origSummary,
  };
}

export function useRequestFeedsGpt(
  props: CommonGptDataType & {
    autoRequest?: boolean;
    filter: any;
  },
) {
  const { filter, autoRequest, promptInfo } = props;

  const {
    status,
    percent,
    renderHtml,
    isEmpty,
    handleStop,
    summary,
    errorType,
    GPTSentiment,
    handleSuccess,
    handleError,
    setPageStatus,
    loadingBefore,
  } = useCommonGptData(props);

  const requestGptData = async (
    promptInfo: PromptConfirmItemType,
    defaultPercent = 0,
  ) => {
    const stopProps = loadingBefore(promptInfo, defaultPercent);

    const url = filter.url_and_uins;

    const videoData = await aigcCreateVideoSummary({
      ...filter,
      task_type: promptInfo.gptOptions,
      model_type: promptInfo.modelType,
      prompt: promptInfo.prompt,
    });

    const urlList = url.map((item: any) => item.comment_uin);
    const { success, fail } = videoData || {};
    const key = urlList[0];
    const failCode = fail?.[key];
    if (failCode) {
      const errorType =
        Number(failCode) === 4000 ? 'GPT_urlError' : 'GPT_failed';
      return handleError(errorType);
    }
    const { data, status, tips } =
      (await getVideoFeedsSummary({
        filter: {
          comment_uins: urlList,
          media_type: filter.media_type,
        },
        promptInfo,
        ...stopProps,
      })) || {};

    if (status === 'cancel') {
      setPageStatus({ status: 'cancel' });
    } else if (status === 'success') {
      handleSuccess(data || '');
    } else {
      handleError(tips);
    }
  };

  useEffect(() => {
    if (autoRequest && promptInfo) {
      requestGptData(promptInfo);
    }
  }, [promptInfo]);

  return {
    status,
    requestGptData,
    percent,
    renderHtml,
    isEmpty,
    handleStop,
    summary,
    errorType,
    GPTSentiment,
  };
}

export function useRequestSteamingGpt(
  props: CommonGptDataType & {
    autoRequest?: boolean;
    filter: any;
    changeShowChildren?: (value: boolean) => void;
  },
) {
  const { filter, autoRequest, changeShowChildren } = props;
  const [loading, setLoading] = useState(true);
  const {
    status,
    percent,
    renderHtml,
    isEmpty,
    handleStop,
    summary,
    errorType, 
    handleSuccess,
    handleError,
    setPageStatus,
    loadingBefore,
  } = useCommonGptData(props);

  const requestGptData = async (defaultPercent = 0) => {
    const stopProps = loadingBefore({}, defaultPercent);
    const { live_id, start_time, end_time } = filter;
    const { data, status, tips } =
      (await getSteamingFeedsSummary({
        filter: { live_id, start_time, end_time },
        ...stopProps,
      })) || {};
    if (status === 'cancel') {
      setPageStatus({ status: 'cancel' });
      changeShowChildren?.(true);
    } else if (status === 'success') {
      handleSuccess(data || '');
    } else {
      handleError(tips);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (filter?.live_id && autoRequest) {
      setLoading(true);
      requestGptData();
    }
  }, [filter]);

  return {
    status,
    requestGptData,
    percent,
    renderHtml,
    isEmpty,
    handleStop,
    summary,
    errorType, 
    loading
  };
}
